import SectionHeader from "../components/common/SectionHeader";
import AuditableFigure from "../components/common/AuditableFigure";
import { useForecast, useEngines } from "../hooks/data";
import { getEngine } from "../config/engines";
import { fmtMoney } from "../lib/format";
import { T } from "../lib/testids";

export default function Forecast() {
  const { data: forecast } = useForecast();
  const { data: engines } = useEngines();

  if (!forecast) {
    return (
      <div className="ambient-bg min-h-full px-8 py-16 text-zinc-500 font-mono text-sm">
        Calculando forecast...
      </div>
    );
  }

  const p10 = forecast.points.find((p) => p.percentile === "p10");
  const p50 = forecast.points.find((p) => p.percentile === "p50");
  const p90 = forecast.points.find((p) => p.percentile === "p90");

  const p50Estimate = {
    label: "estimated_opportunity",
    value: p50.value,
    confidence: 0.5,
    sampleSize: forecast.sampleSize,
    errorMarginPct: 30,
    rationale:
      "Suma de valor estimado × probabilidad de cada oportunidad abierta, agregada por categoría. Rango P10-P90 muestra escenarios pesimista y optimista.",
    lastRecalculatedAt: forecast.computedAt,
  };

  return (
    <div className="ambient-bg min-h-full">
      <div className="px-8 pt-8 pb-6 border-b border-[#17223a]">
        <SectionHeader
          overline={`Proyección de ingresos · ${forecast.horizon}`}
          title="Proyección de ingresos"
          subtitle="Rango probabilístico de ingresos esperados en el horizonte seleccionado. Toda cifra es una estimación auditable."
        />
      </div>

      <div className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-[#17223a] bg-[#0d1321]">
          <div className="p-6 border-r border-[#17223a]">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Escenario pesimista · P10
            </div>
            <div className="font-display font-black text-3xl tabular-nums text-zinc-400">
              {fmtMoney(p10.value.amount)}
            </div>
          </div>
          <div className="p-6 border-r border-[#17223a]" data-testid={T.forecastP50}>
            <AuditableFigure estimate={p50Estimate} size="lg" tone="amber" label="Escenario base · P50" />
          </div>
          <div className="p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Escenario optimista · P90
            </div>
            <div className="font-display font-black text-3xl tabular-nums text-emerald-400">
              {fmtMoney(p90.value.amount)}
            </div>
          </div>
        </div>

        <div className="mt-8 border border-[#17223a] bg-[#0d1321]" data-testid={T.forecastRange}>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4 px-6 py-4 border-b border-[#17223a]">
            Contribución por categoría
          </div>
          <div className="p-6 space-y-4">
            {(engines || []).map((e) => {
              const val = forecast.byEngine[e.id]?.amount || 0;
              const pct = p50.value.amount > 0 ? (val / p50.value.amount) * 100 : 0;
              const cfg = getEngine(e.id);
              return (
                <div key={e.id}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400">
                      {cfg.humanLabel}
                      {e.status !== "live" && (
                        <span className="ml-2 text-[9px] text-zinc-600">(no activo)</span>
                      )}
                    </span>
                    <span className="text-xs font-mono tabular-nums text-zinc-200">
                      {fmtMoney(val)} · {pct.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#101728]">
                    <div
                      className={`h-full ${e.status === "live" ? "bg-amber-500" : "bg-zinc-700"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
